// Grid / tile takeoff for an AREA condition (OST parity, 2026-09-11 — OST's
// Advanced > Grid "Tile W x H, Gap" and its "Tile count (visible)" /
// "Tile count (average)" / "Grid length (visible)" results).
//
// Given a traced polygon (world feet) and a tile grid, count the tiles the
// shape ACTUALLY touches — the number an estimator has to order, partial
// tiles included — as opposed to the plain area ÷ tile arithmetic OST calls
// "average" (also returned, for comparison). The grid is anchored at the
// polygon's own bounding-box corner, the same default OST uses before a
// grid origin is set by hand; a per-instance origin is not modeled yet.
//
// A tile "is visible" when its rectangle intersects the polygon at all:
//   1. any tile corner or its center lies inside the polygon, or
//   2. any polygon vertex lies inside the tile, or
//   3. any polygon edge crosses any tile edge (catches the sliver case where
//      an edge cuts a tile corner without either shape holding the other's
//      points).
// That is exact for the intersection question on simple polygons — no
// sampling gaps — at O(tiles × edges), which is fine for rooms (a 100 × 100
// ft area on 2 × 2 tiles is 2,500 tiles). Above `maxCells` the walk is
// skipped and visible falls back to CEILING(average), flagged by
// `approximate: true`, so a mistaken 0.1-ft tile can't hang the canvas.
//
// Grid length (visible) approximates the runner/tee length a ceiling grid
// needs: each visible tile owns two of its four grid edges (one pitch width
// + one pitch height), the other two belong to its neighbors. Perimeter
// closure is not added. Good enough for a budgetary grid takeoff; a real
// main-runner vs cross-tee split is a later refinement.
//
// The SQL twin (fn_ConditionCalcQuantity, db/37) cannot walk a polygon, so
// the canvas stores this result on the drawn row (GeometryJson '$.grid')
// and RoomTakeoff.jsx re-syncs it when a type's grid changes — see the
// "Visible tile-count sync" effect there.

function pointInPolygon(p, verts) {
  let inside = false;
  for (let i = 0, j = verts.length - 1; i < verts.length; j = i++) {
    const a = verts[i], b = verts[j];
    if ((a.y > p.y) !== (b.y > p.y) && p.x < ((b.x - a.x) * (p.y - a.y)) / (b.y - a.y) + a.x) inside = !inside;
  }
  return inside;
}

function segmentsIntersect(p1, p2, p3, p4) {
  const d = (p2.x - p1.x) * (p4.y - p3.y) - (p2.y - p1.y) * (p4.x - p3.x);
  if (Math.abs(d) < 1e-12) return false; // parallel/collinear — the point tests above cover overlap
  const t = ((p3.x - p1.x) * (p4.y - p3.y) - (p3.y - p1.y) * (p4.x - p3.x)) / d;
  const u = ((p3.x - p1.x) * (p2.y - p1.y) - (p3.y - p1.y) * (p2.x - p1.x)) / d;
  return t >= 0 && t <= 1 && u >= 0 && u <= 1;
}

export function polygonBounds(points) {
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const p of points) {
    if (p.x < minX) minX = p.x; if (p.x > maxX) maxX = p.x;
    if (p.y < minY) minY = p.y; if (p.y > maxY) maxY = p.y;
  }
  return { minX, minY, maxX, maxY };
}

// The tile is tested shrunk by EPS on every side, so a tile that only
// TOUCHES the polygon — sharing a corner point or running along a shared
// edge, as every tile on a grid-aligned room boundary does — is not counted;
// only real overlap of interiors is.
function tileTouchesPolygon(tx0, ty0, tx1, ty1, verts) {
  const EPS = 1e-6;
  const x0 = tx0 + EPS, y0 = ty0 + EPS, x1 = tx1 - EPS, y1 = ty1 - EPS;
  if (x1 <= x0 || y1 <= y0) return false;
  const corners = [{ x: x0, y: y0 }, { x: x1, y: y0 }, { x: x1, y: y1 }, { x: x0, y: y1 }];
  if (pointInPolygon({ x: (x0 + x1) / 2, y: (y0 + y1) / 2 }, verts)) return true;
  for (const c of corners) if (pointInPolygon(c, verts)) return true;
  for (const v of verts) if (v.x > x0 && v.x < x1 && v.y > y0 && v.y < y1) return true;
  for (let i = 0, j = verts.length - 1; i < verts.length; j = i++) {
    const a = verts[j], b = verts[i];
    for (let k = 0; k < 4; k++) {
      if (segmentsIntersect(a, b, corners[k], corners[(k + 1) % 4])) return true;
    }
  }
  return false;
}

// -> { tileWFt, tileHFt, gapIn, pitchWFt, pitchHFt, tileCountVisible,
//      tileCountAverage, gridLengthVisibleFt, approximate }
// or null when the inputs can't define a grid (missing/non-positive tile
// dims, fewer than 3 points).
export function gridTileTakeoff(points, tileWFt, tileHFt, gapIn = 0, { maxCells = 250_000, areaFt2 = null } = {}) {
  const w = +tileWFt, h = +tileHFt, gap = Math.max(0, +gapIn || 0) / 12;
  if (!(w > 0) || !(h > 0) || !Array.isArray(points) || points.length < 3) return null;
  const pitchW = w + gap, pitchH = h + gap;
  const verts = points.map(p => ({ x: +p.x, y: +p.y }));
  const { minX, minY, maxX, maxY } = polygonBounds(verts);
  const area = areaFt2 != null ? +areaFt2 : Math.abs(verts.reduce((s, p, i) => {
    const q = verts[(i + 1) % verts.length];
    return s + p.x * q.y - q.x * p.y;
  }, 0)) / 2;
  const tileCountAverage = area / (pitchW * pitchH);

  const cols = Math.max(1, Math.ceil((maxX - minX) / pitchW + 1e-9));
  const rows = Math.max(1, Math.ceil((maxY - minY) / pitchH + 1e-9));
  const base = { tileWFt: w, tileHFt: h, gapIn: Math.max(0, +gapIn || 0), pitchWFt: pitchW, pitchHFt: pitchH, tileCountAverage: round1(tileCountAverage) };
  if (cols * rows > maxCells) {
    const est = Math.ceil(tileCountAverage);
    return { ...base, tileCountVisible: est, gridLengthVisibleFt: round1(est * (pitchW + pitchH)), approximate: true };
  }

  let visible = 0;
  for (let j = 0; j < rows; j++) {
    const y0 = minY + j * pitchH, y1 = y0 + h;
    for (let i = 0; i < cols; i++) {
      const x0 = minX + i * pitchW, x1 = x0 + w;
      if (tileTouchesPolygon(x0, y0, x1, y1, verts)) visible++;
    }
  }
  return { ...base, tileCountVisible: visible, gridLengthVisibleFt: round1(visible * (pitchW + pitchH)), approximate: false };
}

// Two stored/live grid results agree when the grid definition and both
// counts match — what RoomTakeoff's sync effect compares.
export function gridResultsEqual(a, b) {
  if (!a || !b) return false;
  return +a.tileWFt === +b.tileWFt && +a.tileHFt === +b.tileHFt && +(a.gapIn || 0) === +(b.gapIn || 0)
    && +a.tileCountVisible === +b.tileCountVisible && +a.gridLengthVisibleFt === +b.gridLengthVisibleFt;
}

const round1 = (n) => Math.round(n * 10) / 10;
